import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import EmailIcon from '@mui/icons-material/Email';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import SearchIcon from '@mui/icons-material/Search';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    IconButton,
    InputAdornment,
    MenuItem,
    Popover,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useRoleLabels } from 'src/hooks/useRoleLabels';
import { useRoleConfig } from 'src/context/AppContext';
import { DirectoryMember, DirectoryMemberDetail, PaginatedResponse, ReminderNotification, ROLE_CONFIG, USER_TYPE } from 'src/object-actions/types/types';
import { FadedPaper, TrackedTextField } from 'src/theme/StyledFields';
import { timeAgo } from 'src/utils';
import AutoPaginatingList from '../components/AutoPaginatingList';
import ResponseGraphs from '../components/ResponseGraphs';
import ApiClient from '../config/ApiClient';

function buildRoleFilterOptions(getRoleLabel: (k: 'athlete' | 'coach' | 'parent', o?: { plural?: boolean }) => string) {
    return [
        { value: '', label: 'All Roles' },
        { value: 'athlete', label: getRoleLabel('athlete', { plural: true }) },
        { value: 'coach', label: getRoleLabel('coach', { plural: true }) },
        { value: 'parent', label: getRoleLabel('parent', { plural: true }) },
        { value: 'payer', label: 'Payers' },
        { value: 'unassigned', label: 'Unassigned' },
    ];
}

const ASSESSMENT_FILTER_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'submitted', label: 'Pre Submitted' },
    { value: 'not_submitted', label: 'Pre Not Submitted' },
];

const PROGRAM_STATUS_FILTER_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'not_started', label: 'Not Started' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'behind', label: 'Behind' },
    { value: 'completed', label: 'Completed' },
];

const ACTIVITY_FILTER_OPTIONS = [
    { value: '', label: 'Any Activity' },
    { value: 'active_7d', label: 'Active (7d)' },
    { value: 'active_30d', label: 'Active (30d)' },
    { value: 'inactive_30d', label: 'Inactive (30d+)' },
];

const SORT_OPTIONS = [
    { value: 'name', label: 'Name' },
    { value: 'newest', label: 'Newest' },
    { value: 'oldest', label: 'Oldest' },
    { value: 'most_confident', label: 'Most Confident' },
    { value: 'least_confident', label: 'Least Confident' },
    { value: 'program_start_newest', label: 'Program Start (Newest)' },
    { value: 'program_start_oldest', label: 'Program Start (Oldest)' },
    { value: 'most_complete', label: 'Most Complete' },
    { value: 'least_complete', label: 'Least Complete' },
    { value: 'recent_activity', label: 'Recent Activity' },
];

function ColumnHeaderInfo({ title, tooltipText, children }: { title: string; tooltipText: string; children: React.ReactNode }) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => setAnchorEl(null);

    const open = Boolean(anchorEl);

    return (
        <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
            {children}
            <Tooltip title={tooltipText}>
                <IconButton
                    size="small"
                    onClick={handleClick}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{ p: 0.25 }}
                    aria-label={`Info about ${title}`}
                    data-attr={`directory_${title.toLowerCase()}-info`}
                >
                    <InfoOutlined sx={{ fontSize: 16 }} />
                </IconButton>
            </Tooltip>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                slotProps={{ paper: { sx: { maxWidth: 280, p: 2 } } }}
            >
                <Typography variant="body2">{tooltipText}</Typography>
            </Popover>
        </Box>
    );
}

function RoleChips({
    roles,
    isAdmin,
    roleConfig,
}: {
    roles: USER_TYPE[];
    isAdmin: boolean;
    roleConfig: typeof ROLE_CONFIG;
}) {
    return (
        <Box display="flex" gap={0.5} flexWrap="wrap">
            {isAdmin && (
                <Chip
                    icon={<ShieldOutlined sx={{ fontSize: 14 }} />}
                    label="Admin"
                    size="small"
                    color="error"
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.7rem' }}
                />
            )}
            {roles.map((role) => {
                const config = roleConfig[role];
                if (!config) return null;
                return (
                    <Chip
                        key={role}
                        label={config.label}
                        size="small"
                        color={config.color}
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                    />
                );
            })}
            {roles.length === 0 && !isAdmin && (
                <Chip label="No role" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem', opacity: 0.5 }} />
            )}
        </Box>
    );
}

function AssessmentStatus({ hasPre, hasPost }: { hasPre: boolean; hasPost: boolean }) {
    return (
        <Box display="flex" gap={1} alignItems="center">
            <Box display="flex" alignItems="center" gap={0.3}>
                {hasPre ? (
                    <CheckCircleOutline color="success" sx={{ fontSize: 16 }} />
                ) : (
                    <RadioButtonUnchecked sx={{ fontSize: 16, opacity: 0.3 }} />
                )}
                <Typography variant="caption" color="text.secondary">Pre</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.3}>
                {hasPost ? (
                    <CheckCircleOutline color="success" sx={{ fontSize: 16 }} />
                ) : (
                    <RadioButtonUnchecked sx={{ fontSize: 16, opacity: 0.3 }} />
                )}
                <Typography variant="caption" color="text.secondary">Post</Typography>
            </Box>
        </Box>
    );
}

function ProgramCompletion({ member }: { member: DirectoryMember }) {
    if (!member.program_start_date) {
        return <Typography variant="caption" color="text.secondary">Not started</Typography>;
    }
    return (
        <Box display="flex" alignItems="center" justifyContent="center" gap={0.75}>
            <Typography variant="body2" fontWeight={600}>
                {member.session_completion_pct}%
            </Typography>
            {member.session_on_track === true && (
                <Chip label="On track" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            )}
            {member.session_on_track === false && (
                <Chip label="Behind" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            )}
        </Box>
    );
}

function buildEntityFromScores(member: DirectoryMember) {
    const scores = member.category_scores;
    if (!scores) return null;
    const hasAnyScore = Object.values(scores).some(v => v !== null);
    if (!hasAnyScore) return null;
    return {
        category_performance_mindset: scores.performance_mindset,
        category_emotional_regulation: scores.emotional_regulation,
        category_confidence: scores.confidence,
        category_resilience_motivation: scores.resilience_motivation,
        category_concentration: scores.concentration,
        category_leadership: scores.leadership,
        category_mental_wellbeing: scores.mental_wellbeing,
        category_communication: scores.communication,
    };
}

type ReminderSendResult = { success: boolean; error?: string };

function ReminderButton({ label, onSend, lastSentAt }: { label: string; onSend: () => Promise<ReminderSendResult>; lastSentAt?: string | null }) {
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleClick = async () => {
        setSending(true);
        setError(null);
        try {
            const result = await onSend();
            if (result.success) setSent(true);
            else setError(result.error || 'Failed to send reminder');
        } catch (e) {
            setError(e instanceof Error ? e.message : 'An error occurred');
        } finally {
            setSending(false);
        }
    };

    if (sent) {
        return <Typography variant="body2" color="success.main">Reminder sent!</Typography>;
    }

    return (
        <Box display="flex" flexDirection="column" gap={0.5}>
            <Box display="flex" alignItems="center" gap={1}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EmailIcon />}
                    onClick={handleClick}
                    disabled={sending}
                    sx={{ textTransform: 'none' }}
                >
                    {sending ? 'Sending...' : label}
                </Button>
                {lastSentAt && (
                    <Typography variant="caption" color="text.secondary">
                        Last sent {timeAgo(lastSentAt)}
                    </Typography>
                )}
            </Box>
            {error && <Typography variant="caption" color="error">{error}</Typography>}
        </Box>
    );
}

function MemberExpandedDetail({
    member,
    applyRoleLabels: applyLabels,
}: {
    member: DirectoryMember;
    applyRoleLabels: (text: string) => string;
}) {
    const entity = buildEntityFromScores(member);
    const [detail, setDetail] = useState<DirectoryMemberDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState<string | null>(null);

    const fetchDetail = useCallback(async () => {
        setDetailLoading(true);
        setDetailError(null);
        try {
            const response = await ApiClient.get<DirectoryMemberDetail>(
                `/api/org-directory/${member.uo_id}/detail`
            );
            if (response.success && response.data) {
                setDetail(response.data);
            } else {
                setDetailError(response.error || 'Failed to load details');
            }
        } catch (e) {
            setDetailError(e instanceof Error ? e.message : 'Failed to load details');
        } finally {
            setDetailLoading(false);
        }
    }, [member.uo_id]);

    useEffect(() => {
        fetchDetail();
    }, [fetchDetail]);

    const lastAssessmentReminder = detail?.last_reminder_notifications?.find(
        (n: ReminderNotification) => n.notification_type === 'assessment-reminder'
    )?.sent_at;
    const lastCoachReminder = detail?.last_reminder_notifications?.find(
        (n: ReminderNotification) => n.notification_type === 'coach-publish-reminder'
    )?.sent_at;

    const firstAssignment = detail?.assignments?.[0];

    const handleSendAssessmentReminder = async (): Promise<ReminderSendResult> => {
        const paId = firstAssignment?.paymentassignment_id;
        if (!paId) return { success: false, error: 'Assignment not found' };
        const res = await ApiClient.post(
            `/api/payment-assignments/${paId}/send-assessment-reminder/`, {}
        );
        return { success: res.success, error: res.error };
    };

    const handleSendCoachReminder = async (): Promise<ReminderSendResult> => {
        const res = await ApiClient.post('/api/org-directory/send-coach-reminder', {
            coach_id: member.id,
        });
        return { success: res.success, error: res.error };
    };

    const showAssessmentReminder = member.roles.includes('athlete')
        && firstAssignment && !firstAssignment.pre_assessment_submitted_at;
    const showCoachReminder = member.roles.includes('coach');

    return (
        <Box sx={{ p: 2, pl: 4, display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
            {entity && (
                <Box sx={{ flex: '1 1 300px', minWidth: 280, maxWidth: 500 }}>
                    <ResponseGraphs entity={entity} height={220} />
                </Box>
            )}
            <Box sx={{ flex: '1 1 200px' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Email:</strong> {member.email || '—'}
                </Typography>
                {detail?.last_activity_at && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Last active:</strong> {timeAgo(detail.last_activity_at)}
                    </Typography>
                )}
                {member.joined_at && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Joined:</strong> {new Date(member.joined_at).toLocaleDateString()}
                    </Typography>
                )}
                {firstAssignment?.assessment_id && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Assessment:</strong> #{firstAssignment.assessment_id}
                    </Typography>
                )}
                <Box mt={2} display="flex" gap={1}>
                    <Chip
                        label="View Progress"
                        component={Link}
                        to={`/program-progress/${member.id}`}
                        clickable
                        size="small"
                    />
                </Box>
            </Box>

            {detailLoading && (
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            )}
            {detailError && (
                <Alert severity="error" sx={{ width: '100%' }}>{detailError}</Alert>
            )}
            {detail && (
                <Box sx={{ width: '100%' }}>
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" flexWrap="wrap" gap={2}>
                        {showAssessmentReminder && (
                            <ReminderButton
                                label="Send Assessment Reminder"
                                onSend={handleSendAssessmentReminder}
                                lastSentAt={lastAssessmentReminder}
                            />
                        )}
                        {showCoachReminder && (
                            <ReminderButton
                                label={applyLabels(`Remind ${member.first_name || 'Coach'} to Follow Up`)}
                                onSend={handleSendCoachReminder}
                                lastSentAt={lastCoachReminder}
                            />
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
}

const DirectoryPage: React.FC = () => {
    const { getRoleLabel, applyRoleLabels } = useRoleLabels();
    const roleConfig = useRoleConfig();
    const roleFilterOptions = useMemo(
        () => buildRoleFilterOptions(getRoleLabel),
        [getRoleLabel]
    );
    const [urlParams] = useSearchParams();
    const [members, setMembers] = useState<DirectoryMember[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(25);

    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState(urlParams.get('role') || '');
    const [assessmentFilter, setAssessmentFilter] = useState(urlParams.get('pre_assessment') || '');
    const [programStatusFilter, setProgramStatusFilter] = useState(urlParams.get('program_status') || '');
    const [activityFilter, setActivityFilter] = useState(urlParams.get('activity') || '');
    const [sortBy, setSortBy] = useState(urlParams.get('sort_by') || 'name');
    const [expandedId, setExpandedId] = useState<number | null>(null);

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setDebouncedSearch(search);
        }, 400);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [search]);

    const basePath = useMemo(() => {
        const params = new URLSearchParams();
        if (debouncedSearch) params.set('search', debouncedSearch);
        if (roleFilter) params.set('role', roleFilter);
        if (assessmentFilter) params.set('pre_assessment', assessmentFilter);
        if (programStatusFilter) params.set('program_status', programStatusFilter);
        if (activityFilter) params.set('activity', activityFilter);
        if (sortBy !== 'name') params.set('sort_by', sortBy);
        const qs = params.toString();
        return `/api/org-directory${qs ? `?${qs}` : ''}`;
    }, [debouncedSearch, roleFilter, assessmentFilter, programStatusFilter, activityFilter, sortBy]);

    const handleSuccess = (data: PaginatedResponse<DirectoryMember>) => {
        if (data && data.results) {
            setMembers(prev => [...prev, ...(data.results as DirectoryMember[])]);
            setTotalCount(data.count);
            setOffset(data.offset);
            setLimit(data.limit);
        }
    };

    const handleError = (err: string) => {
        setError(err);
    };

    useEffect(() => {
        setMembers([]);
        setOffset(0);
        setError(null);
        setExpandedId(null);
    }, [debouncedSearch, roleFilter, assessmentFilter, programStatusFilter, activityFilter, sortBy]);

    const toggleExpand = (id: number) => {
        setExpandedId(prev => prev === id ? null : id);
    };

    if (error) {
        return (
            <Box p={2} data-testid="directory-page">
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box p={2} data-testid="directory-page">
            <FadedPaper sx={{ mb: 2 }}>
                <Toolbar sx={{ flexWrap: 'wrap', gap: 1, p: 1 }}>
                    <Typography variant="h6" sx={{ flexGrow: 1, minWidth: 200 }}>
                        Organization Directory ({totalCount})
                    </Typography>
                    <TrackedTextField
                        size="small"
                        placeholder="Search name or email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ minWidth: 220 }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }
                        }}
                        data-attr="directory_search"
                    />
                    <TrackedTextField
                        select
                        label="Role"
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        size="small"
                        sx={{ minWidth: 140 }}
                        data-attr="directory_role-filter"
                    >
                        {roleFilterOptions.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </TrackedTextField>
                    <TrackedTextField
                        select
                        label="Assessment"
                        value={assessmentFilter}
                        onChange={(e) => setAssessmentFilter(e.target.value)}
                        size="small"
                        sx={{ minWidth: 160 }}
                        data-attr="directory_assessment-filter"
                    >
                        {ASSESSMENT_FILTER_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </TrackedTextField>
                    <TrackedTextField
                        select
                        label="Program"
                        value={programStatusFilter}
                        onChange={(e) => setProgramStatusFilter(e.target.value)}
                        size="small"
                        sx={{ minWidth: 150 }}
                        data-attr="directory_program-status-filter"
                    >
                        {PROGRAM_STATUS_FILTER_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </TrackedTextField>
                    <TrackedTextField
                        select
                        label="Activity"
                        value={activityFilter}
                        onChange={(e) => setActivityFilter(e.target.value)}
                        size="small"
                        sx={{ minWidth: 150 }}
                        data-attr="directory_activity-filter"
                    >
                        {ACTIVITY_FILTER_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </TrackedTextField>
                    <TrackedTextField
                        select
                        label="Sort By"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        size="small"
                        sx={{ minWidth: 160 }}
                        data-attr="directory_sort-by"
                    >
                        {SORT_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </TrackedTextField>
                </Toolbar>
            </FadedPaper>

            <AutoPaginatingList<DirectoryMember>
                basePath={basePath}
                count={totalCount}
                limit={limit}
                offset={offset}
                onSuccess={handleSuccess}
                onError={handleError}
            >
                <TableContainer component={FadedPaper} data-testid="directory-table">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell />
                                <TableCell>Member</TableCell>
                                <TableCell>Roles</TableCell>
                                <TableCell align="center">
                                    <ColumnHeaderInfo
                                        title="Score"
                                        tooltipText="The sum of category averages pre and post (40 max)."
                                    >
                                        Score
                                    </ColumnHeaderInfo>
                                </TableCell>
                                <TableCell align="center">
                                    <ColumnHeaderInfo
                                        title="Assessments"
                                        tooltipText="Indicates which assessments have been taken (Pre and Post)."
                                    >
                                        Assessments
                                    </ColumnHeaderInfo>
                                </TableCell>
                                <TableCell>
                                    <ColumnHeaderInfo
                                        title="Program Start"
                                        tooltipText="When the 12-week lesson program began (first session started, else lesson plan delivered, else first assessment response). Members still in baseline intake show 'Not started'."
                                    >
                                        Program Start
                                    </ColumnHeaderInfo>
                                </TableCell>
                                <TableCell align="center">
                                    <ColumnHeaderInfo
                                        title="Completion"
                                        tooltipText="Percent of the 12 sessions completed, with an on-track / behind indicator based on the expected weekly pace since program start."
                                    >
                                        Completion
                                    </ColumnHeaderInfo>
                                </TableCell>
                                <TableCell>Joined</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {members.map((member) => (
                                <React.Fragment key={member.id}>
                                    <TableRow
                                        hover
                                        onClick={() => toggleExpand(member.id)}
                                        data-testid="directory-member-row"
                                        sx={{ cursor: 'pointer', '& > *': { borderBottom: expandedId === member.id ? 'none' : undefined } }}
                                    >
                                        <TableCell sx={{ width: 48, pr: 0 }}>
                                            <Avatar
                                                src={member.avatar || undefined}
                                                sx={{ width: 32, height: 32, fontSize: '0.85rem' }}
                                            >
                                                {(member.first_name?.[0] || member.username?.[0] || '?').toUpperCase()}
                                            </Avatar>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 500 }}
                                            >
                                                {member.first_name || member.last_name
                                                    ? `${member.first_name} ${member.last_name}`.trim()
                                                    : member.username}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <RoleChips roles={member.roles} isAdmin={member.is_admin} roleConfig={roleConfig} />
                                        </TableCell>
                                        <TableCell align="center">
                                            {member.category_total_score != null ? (
                                                <Typography variant="body2" fontWeight={600}>
                                                    {member.category_total_score.toFixed(1)}
                                                </Typography>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">--</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <AssessmentStatus
                                                hasPre={member.has_pre_assessment}
                                                hasPost={member.has_post_assessment}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                                {member.program_start_date
                                                    ? new Date(member.program_start_date).toLocaleDateString()
                                                    : 'Not started'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <ProgramCompletion member={member} />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                                {member.joined_at ? timeAgo(member.joined_at) : '--'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ width: 40 }}>
                                            <IconButton size="small">
                                                {expandedId === member.id ? <ExpandLess /> : <ExpandMore />}
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={9} sx={{ p: 0, border: 'none' }}>
                                            <Collapse in={expandedId === member.id} timeout="auto" unmountOnExit>
                                                <MemberExpandedDetail member={member} applyRoleLabels={applyRoleLabels} />
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                            {members.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body1" color="text.secondary">
                                            No members found.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </AutoPaginatingList>
        </Box>
    );
};

export default DirectoryPage;
